import { useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loading } from "@/deps/ui-library";
import { api } from "@/api";
import {
  useBill,
  useCreditNote,
  useDebitNote,
  useInvoice,
  usePurchaseOrder,
  useQuotation,
} from "@/api/queries";
import type { Party } from "@/api/types";
import { NewBillPage } from "@/features/purchases/bills/NewBillPage";
import { PurchaseOrderEditorPage } from "@/features/purchases/orders/PurchaseOrderEditorPage";
import { NewDebitNotePage } from "@/features/purchases/debit-notes/NewDebitNotePage";
import { NewCreditNotePage } from "@/features/sales/credit-notes/NewCreditNotePage";
import { NewInvoicePage } from "@/features/sales/invoices/NewInvoicePage";
import { QuotationEditorPage } from "@/features/sales/quotations/QuotationEditorPage";

/**
 * The document editors take a draft and the party it names. These load both, so an editor's state
 * starts complete rather than filling in after its first render. A document that is no longer a draft
 * says so here: the API would refuse the save anyway, and the person should not type first.
 */
const useParty = (id: string | undefined) =>
  useQuery<Party>({
    queryKey: ["parties", "one", id],
    queryFn: () => api.parties.get(id!),
    enabled: id !== undefined,
  });

const notADraft = (
  <p className="text-bad-text p-6 text-sm">This has a number now, so it can no longer be edited.</p>
);

export function EditInvoicePage() {
  const { id } = useParams({ from: "/sales/invoices/$id/edit" });
  const { data: invoice } = useInvoice(id);
  const { data: customer } = useParty(invoice?.customerId);
  if (!invoice || !customer) return <Loading />;
  if (invoice.status !== "draft") return notADraft;
  return <NewInvoicePage key={invoice.id} editing={{ invoice, customer }} />;
}

export function EditQuotationPage() {
  const { id } = useParams({ from: "/sales/quotations/$id/edit" });
  const { data: quotation } = useQuotation(id);
  const { data: customer } = useParty(quotation?.customerId);
  if (!quotation || !customer) return <Loading />;
  if (quotation.status !== "draft") return notADraft;
  return <QuotationEditorPage key={quotation.id} editing={{ quotation, customer }} />;
}

export function EditPurchaseOrderPage() {
  const { id } = useParams({ from: "/purchases/orders/$id/edit" });
  const { data: order } = usePurchaseOrder(id);
  const { data: supplier } = useParty(order?.supplierId);
  if (!order || !supplier) return <Loading />;
  if (order.status !== "draft") return notADraft;
  return <PurchaseOrderEditorPage key={order.id} editing={{ order, supplier }} />;
}

export function EditCreditNotePage() {
  const { id } = useParams({ from: "/sales/credit-notes/$id/edit" });
  const { data: note } = useCreditNote(id);
  const { data: customer } = useParty(note?.customerId);
  // The note holds the number of what it corrects, and the editor chooses from invoices by id.
  const { data: corrected } = useQuery({
    queryKey: ["invoices", "correctedBy", id, note?.appliedTo],
    queryFn: async () =>
      (await api.invoices.list({ q: note!.appliedTo! })).items.find(
        (i) => i.number === note!.appliedTo,
      ) ?? null,
    enabled: note !== undefined && Boolean(note.appliedTo),
  });
  if (!note || !customer || (note.appliedTo && corrected === undefined)) return <Loading />;
  if (note.status !== "draft") return notADraft;
  return (
    <NewCreditNotePage
      key={note.id}
      editing={{ note, customer, invoiceId: note.precedingInvoiceId ?? corrected?.id ?? "" }}
    />
  );
}

export function EditBillPage() {
  const { id } = useParams({ from: "/purchases/bills/$id/edit" });
  const { data: bill } = useBill(id);
  const { data: supplier } = useParty(bill?.supplierId);
  if (!bill || !supplier) return <Loading />;
  if (bill.status === "approved") return notADraft;
  return <NewBillPage key={bill.id} editing={{ bill, supplier }} />;
}

export function EditDebitNotePage() {
  const { id } = useParams({ from: "/purchases/debit-notes/$id/edit" });
  const { data: note } = useDebitNote(id);
  const { data: supplier } = useParty(note?.supplierId);
  if (!note || !supplier) return <Loading />;
  if (note.status === "approved") return notADraft;
  return <NewDebitNotePage key={note.id} editing={{ note, supplier }} />;
}

/** A new debit note, opened from a bill when the link names one. */
export function NewDebitNoteRoute() {
  const { bill } = useSearch({ from: "/purchases/debit-notes/new" });
  return <NewDebitNotePage billFromLink={bill} />;
}
